import React, { useState } from 'react';
import './FeedbackButtons.css';

interface FeedbackButtonsProps {
  messageId: string;
  userQuestion: string;
  botResponse: string;
  appMode: 'pawco' | 'genius-dog';
  onFeedbackSubmit?: (feedback: FeedbackData) => void;
}

export interface FeedbackData {
  messageId: string;
  userQuestion: string;
  botResponse: string;
  feedbackType: 'positive' | 'negative';
  comment?: string;
  appMode: 'pawco' | 'genius-dog';
  timestamp: string;
}

const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({
  messageId,
  userQuestion,
  botResponse,
  appMode,
  onFeedbackSubmit
}) => {
  const [feedback, setFeedback] = useState<'positive' | 'negative' | null>(null);
  const [showCommentBox, setShowCommentBox] = useState(false);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleFeedbackClick = async (type: 'positive' | 'negative') => {
    if (submitted) return;
    
    setFeedback(type);
    setShowCommentBox(true);
  };

  const handleSubmit = async () => {
    if (!feedback || isSubmitting) return;

    setIsSubmitting(true);

    const feedbackData: FeedbackData = {
      messageId,
      userQuestion,
      botResponse,
      feedbackType: feedback,
      comment: comment.trim() || undefined,
      appMode,
      timestamp: new Date().toISOString()
    };

    try {
      // Call the parent's feedback handler
      if (onFeedbackSubmit) {
        await onFeedbackSubmit(feedbackData);
      }
      
      setSubmitted(true);
      setShowCommentBox(false);
      
      // Auto-hide after 2 seconds
      setTimeout(() => {
        setShowCommentBox(false);
      }, 2000);
    } catch (error) {
      console.error('Failed to submit feedback:', error);
      alert('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkipComment = () => {
    setComment('');
    handleSubmit();
  };

  if (submitted) {
    return (
      <div className="feedback-container">
        <div className="feedback-submitted">
          <i className="fas fa-check-circle"></i>
          <span>Thank you for your feedback!</span>
        </div>
      </div>
    );
  }

  return (
    <div className="feedback-container">
      <div className="feedback-buttons">
        <button
          className={`feedback-btn thumbs-up ${feedback === 'positive' ? 'active' : ''}`}
          onClick={() => handleFeedbackClick('positive')}
          disabled={submitted}
          title="Helpful response"
        >
          <i className={`fas fa-thumbs-up ${feedback === 'positive' ? 'fa-solid' : 'fa-regular'}`}></i>
        </button>
        <button
          className={`feedback-btn thumbs-down ${feedback === 'negative' ? 'active' : ''}`}
          onClick={() => handleFeedbackClick('negative')}
          disabled={submitted}
          title="Not helpful"
        >
          <i className={`fas fa-thumbs-down ${feedback === 'negative' ? 'active' : 'fa-regular'}`}></i>
        </button>
      </div>

      {showCommentBox && !submitted && (
        <div className="feedback-comment-box">
          <textarea
            className="feedback-textarea"
            placeholder={`${feedback === 'positive' ? 'What did you like?' : 'How can we improve?'} (optional)`}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            maxLength={500}
          />
          <div className="feedback-actions">
            <button
              className="feedback-submit-btn"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Submitting...
                </>
              ) : (
                <>
                  <i className="fas fa-paper-plane"></i>
                  Submit
                </>
              )}
            </button>
            <button
              className="feedback-skip-btn"
              onClick={handleSkipComment}
              disabled={isSubmitting}
            >
              Skip
            </button>
          </div>
          <div className="feedback-char-count">
            {comment.length}/500
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackButtons;
